import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Play, ArrowRight, Camera, Wand2, Check, Loader2 } from "lucide-react";
import { useApp } from "../AppContext";

/**
 * VisionDemoInteractive — Démo interactive pour la landing publique.
 * Photo AVANT (cuisine dépassée) → 3 rendus AI (Moderne / Classique / Luxe)
 * → estimation approximative → CTA d'inscription.
 * Utilise des assets pré-cachés dans /public/vision-demo/ pour un affichage instantané.
 */
const STYLES = [
  { id: "modern",  labelFr: "Moderne",  labelEn: "Modern",  price: 48200, img: "/vision-demo/after_modern.png" },
  { id: "classic", labelFr: "Classique", labelEn: "Classic", price: 39800, img: "/vision-demo/after_classic.png" },
  { id: "luxury",  labelFr: "Luxe",     labelEn: "Luxury",  price: 72500, img: "/vision-demo/after_luxury.png" },
];

export default function VisionDemoInteractive() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  const [step, setStep] = useState(0); // 0=idle, 1=analyzing, 2=variants, 3=quote
  const [chosen, setChosen] = useState(null);

  const startDemo = async () => {
    setStep(1);
    // Analyse simulée (2s pour l'effet)
    setTimeout(() => setStep(2), 1800);
  };
  const pickStyle = (style) => {
    setChosen(style);
    setStep(3);
  };
  const reset = () => { setStep(0); setChosen(null); };

  const T = isFr ? {
    tagline: "DÉMO INTERACTIVE",
    title: "Essayez PROGÈRE Vision en 5 secondes",
    subtitle: "Cliquez sur « Lancer la démo » — nous transformons cette vraie photo de cuisine en 3 rénovations. Aucune inscription.",
    startBtn: "Lancer la démo",
    analyzing: "Analyse par PROGÈRE AI…",
    chooseStyle: "Choisissez votre style favori",
    seeEstimate: "Voir l'estimation",
    estimate: "Estimation approximative",
    yourQuote: "Ce type de rénovation est estimé à",
    incl: "incl. TPS + TVQ · main-d'œuvre + matériaux · Québec 2026",
    disclaimer: "Estimation générée par PROGÈRE AI · validation humaine requise avant tout engagement contractuel",
    cta: "Créer mon compte gratuit",
    ctaHint: "14 jours d'essai · 3 devis IA offerts · aucune carte requise",
    reset: "Recommencer",
    beforeLabel: "Photo actuelle",
    ...Object.fromEntries(STYLES.map(s => [s.id, s.labelFr])),
  } : {
    tagline: "INTERACTIVE DEMO",
    title: "Try PROGÈRE Vision in 5 seconds",
    subtitle: "Click 'Start demo' — we transform this real kitchen photo into 3 renovations. No sign-up needed.",
    startBtn: "Start demo",
    analyzing: "Analyzing with PROGÈRE AI…",
    chooseStyle: "Pick your favourite style",
    seeEstimate: "See estimate",
    estimate: "Rough estimate",
    yourQuote: "This renovation type is estimated at",
    incl: "incl. GST + QST · labor + materials · Quebec 2026",
    disclaimer: "Estimate generated by PROGÈRE AI · human validation required before any contract",
    cta: "Create my free account",
    ctaHint: "14-day trial · 3 free AI quotes · no card required",
    reset: "Restart",
    beforeLabel: "Current photo",
    ...Object.fromEntries(STYLES.map(s => [s.id, s.labelEn])),
  };

  const fmt = (n) => new Intl.NumberFormat(isFr ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);

  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 py-16"
      data-testid="vision-demo-section"
    >
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full"
          style={{
            background: "color-mix(in srgb, var(--pg-gold) 12%, transparent)",
            border: "1px solid color-mix(in srgb, var(--pg-gold) 36%, transparent)",
            color: "var(--pg-gold)",
          }}>
          <Sparkles size={11} /> {T.tagline}
        </div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black" style={{ color: "var(--pg-text-primary)" }}>
          <span style={{ color: "var(--pg-gold)" }}>PRO</span><span style={{ color: "var(--pg-red)" }}>GÈRE</span>{" "}
          <span>{isFr ? "VISION" : "VISION"}</span>
        </h2>
        <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base" style={{ color: "var(--pg-text-secondary)" }}>{T.subtitle}</p>
      </div>

      <div className="rounded-3xl p-4 sm:p-8" style={{
        background: "linear-gradient(145deg, rgba(24,25,30,0.94), rgba(16,17,21,0.98))",
        border: "1px solid color-mix(in srgb, var(--pg-gold) 32%, var(--pg-border))",
        boxShadow: "0 20px 60px rgba(0,0,0,0.32), 0 0 30px rgba(242,188,46,0.06)",
      }}>
        {step === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <img src="/vision-demo/before.png" alt="Kitchen before"
                className="rounded-2xl w-full aspect-square object-cover"
                style={{ border: "1px solid var(--pg-border)" }}
                data-testid="vision-demo-before" />
              <div className="mt-2 text-center text-xs font-semibold" style={{ color: "var(--pg-text-muted)" }}>{T.beforeLabel}</div>
            </div>
            <div>
              <div className="rounded-2xl p-6" style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)" }}>
                <Camera size={28} style={{ color: "var(--pg-gold)" }} />
                <h3 className="mt-3 font-heading text-xl font-bold" style={{ color: "var(--pg-text-primary)" }}>
                  {isFr ? "Cette cuisine peut renaître" : "This kitchen can be reborn"}
                </h3>
                <p className="mt-2 text-sm" style={{ color: "var(--pg-text-secondary)" }}>
                  {isFr
                    ? "PROGÈRE AI analyse la photo, propose 3 styles et calcule le prix des travaux — en 30 secondes."
                    : "PROGÈRE AI analyzes the photo, proposes 3 styles, and estimates work costs — in 30 seconds."}
                </p>
                <button
                  onClick={startDemo}
                  data-testid="vision-demo-start"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-5 py-3 rounded-full transition hover:brightness-110"
                  style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
                >
                  <Play size={14} /> {T.startBtn}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="text-center py-12" data-testid="vision-demo-analyzing">
            <div className="relative w-40 h-40 mx-auto">
              <img src="/vision-demo/before.png" alt="analyzing"
                className="rounded-2xl w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 size={40} className="animate-spin" style={{ color: "var(--pg-gold)" }} />
              </div>
            </div>
            <div className="mt-4 text-sm font-semibold" style={{ color: "var(--pg-gold)" }}>{T.analyzing}</div>
          </div>
        )}

        {step >= 2 && (
          <>
            <div className="text-center mb-6">
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>
                {step === 2 ? T.chooseStyle : T.estimate}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {STYLES.map((s) => {
                const isSelected = chosen?.id === s.id;
                const dim = step === 3 && !isSelected;
                return (
                  <button
                    key={s.id}
                    onClick={() => step === 2 && pickStyle(s)}
                    disabled={step !== 2}
                    data-testid={`vision-demo-variant-${s.id}`}
                    className="rounded-2xl overflow-hidden text-left transition"
                    style={{
                      background: "var(--pg-bg-tertiary)",
                      border: `2px solid ${isSelected ? "var(--pg-gold)" : "var(--pg-border)"}`,
                      opacity: dim ? 0.35 : 1,
                      cursor: step === 2 ? "pointer" : "default",
                      transform: isSelected ? "scale(1.02)" : "scale(1)",
                    }}
                  >
                    <img src={s.img} alt={s.labelFr} className="w-full aspect-square object-cover" />
                    <div className="p-3 flex items-center justify-between">
                      <span className="font-semibold text-sm" style={{ color: "var(--pg-text-primary)" }}>
                        {isFr ? s.labelFr : s.labelEn}
                      </span>
                      {isSelected && step === 3 && (
                        <Check size={16} style={{ color: "var(--pg-gold)" }} />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 3 && chosen && (
          <div className="mt-8 text-center" data-testid="vision-demo-estimate">
            <div className="text-sm" style={{ color: "var(--pg-text-secondary)" }}>{T.yourQuote}</div>
            <div className="mt-2 text-4xl sm:text-5xl font-black" style={{ color: "var(--pg-gold)" }}>
              {fmt(chosen.price)}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--pg-text-muted)" }}>{T.incl}</div>

            <div className="mt-6 rounded-xl p-3 max-w-xl mx-auto text-xs"
              style={{ background: "color-mix(in srgb, var(--pg-warning) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--pg-warning) 40%, transparent)", color: "var(--pg-text-secondary)" }}>
              {T.disclaimer}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link
                to="/register"
                data-testid="vision-demo-cta"
                className="inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-full transition hover:brightness-110"
                style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
              >
                <Wand2 size={14} /> {T.cta} <ArrowRight size={14} />
              </Link>
              <button onClick={reset} data-testid="vision-demo-reset"
                className="text-xs font-semibold px-4 py-2 rounded-full"
                style={{ background: "transparent", color: "var(--pg-text-muted)", border: "1px solid var(--pg-border)" }}>
                {T.reset}
              </button>
            </div>
            <div className="mt-2 text-[11px]" style={{ color: "var(--pg-text-muted)" }}>{T.ctaHint}</div>
          </div>
        )}
      </div>
    </section>
  );
}
